import { useRef, useCallback, useEffect } from "react";
import {
  GENERATION_TIMEOUT_MS,
  type PendingGuide,
  type PendingDirectGuide,
} from "./usePendingGuidesPersistence";

// Fallback check interval when realtime events are delayed/missed.
// Frequent enough for responsive mobile UX without excessive load.
export const FALLBACK_CHECK_INTERVAL_MS = 30 * 1000; // 30 seconds

interface UseGuideTimeoutOptions {
  onTimeout: (sopId: string, sopTitle: string) => void;
  onDirectTimeout: (jobId: string, processName: string) => void;
  onFallbackCheck?: (jobId: string, sopId: string) => void;
  onDirectFallbackCheck?: (jobId: string) => void;
}

export function useGuideTimeout({
  onTimeout,
  onDirectTimeout,
  onFallbackCheck,
  onDirectFallbackCheck,
}: UseGuideTimeoutOptions) {
  // Track timeout IDs for cleanup
  const timeoutRefs = useRef<Map<string, NodeJS.Timeout>>(new Map());
  // Track timeout IDs for direct guides
  const directTimeoutRefs = useRef<Map<string, NodeJS.Timeout>>(new Map());
  // Track recurring fallback check interval IDs for cleanup
  const fallbackCheckRefs = useRef<Map<string, NodeJS.Timeout>>(new Map());
  // Track recurring fallback check interval IDs for direct guides
  const directFallbackCheckRefs = useRef<Map<string, NodeJS.Timeout>>(
    new Map(),
  );

  // Set up timeout for a pending guide
  const startTimeout = useCallback(
    (sopId: string, sopTitle: string, startedAt: number, jobId?: string) => {
      // Clear any existing timeout for this SOP
      const existingTimeout = timeoutRefs.current.get(sopId);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }
      const existingFallback = fallbackCheckRefs.current.get(sopId);
      if (existingFallback) {
        clearInterval(existingFallback);
      }

      const elapsed = Date.now() - startedAt;
      const remainingTime = Math.max(0, GENERATION_TIMEOUT_MS - elapsed);

      if (remainingTime > 0) {
        // Set up timeout
        const timeoutId = setTimeout(() => {
          onTimeout(sopId, sopTitle);
          timeoutRefs.current.delete(sopId);
          const fallbackInterval = fallbackCheckRefs.current.get(sopId);
          if (fallbackInterval) {
            clearInterval(fallbackInterval);
          }
          fallbackCheckRefs.current.delete(sopId);
        }, remainingTime);
        timeoutRefs.current.set(sopId, timeoutId);

        // Set up recurring fallback checks while the job is pending.
        if (jobId && onFallbackCheck) {
          if (elapsed >= FALLBACK_CHECK_INTERVAL_MS) {
            onFallbackCheck(jobId, sopId);
          }
          const fallbackId = setInterval(() => {
            onFallbackCheck(jobId, sopId);
          }, FALLBACK_CHECK_INTERVAL_MS);
          fallbackCheckRefs.current.set(sopId, fallbackId);
        }
      } else {
        // Job has exceeded timeout - trigger immediately
        onTimeout(sopId, sopTitle);
      }
    },
    [onTimeout, onFallbackCheck],
  );

  // Set up timeout for a direct guide
  const startDirectTimeout = useCallback(
    (jobId: string, processName: string, startedAt: number) => {
      // Clear any existing timeout for this job
      const existingTimeout = directTimeoutRefs.current.get(jobId);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }
      const existingFallback = directFallbackCheckRefs.current.get(jobId);
      if (existingFallback) {
        clearInterval(existingFallback);
      }

      const elapsed = Date.now() - startedAt;
      const remainingTime = Math.max(0, GENERATION_TIMEOUT_MS - elapsed);

      if (remainingTime > 0) {
        // Set up timeout
        const timeoutId = setTimeout(() => {
          onDirectTimeout(jobId, processName);
          directTimeoutRefs.current.delete(jobId);
          const fallbackInterval = directFallbackCheckRefs.current.get(jobId);
          if (fallbackInterval) {
            clearInterval(fallbackInterval);
          }
          directFallbackCheckRefs.current.delete(jobId);
        }, remainingTime);
        directTimeoutRefs.current.set(jobId, timeoutId);

        // Set up recurring fallback checks while the job is pending.
        if (onDirectFallbackCheck) {
          if (elapsed >= FALLBACK_CHECK_INTERVAL_MS) {
            onDirectFallbackCheck(jobId);
          }
          const fallbackId = setInterval(() => {
            onDirectFallbackCheck(jobId);
          }, FALLBACK_CHECK_INTERVAL_MS);
          directFallbackCheckRefs.current.set(jobId, fallbackId);
        }
      } else {
        // Job has exceeded timeout - trigger immediately
        onDirectTimeout(jobId, processName);
      }
    },
    [onDirectTimeout, onDirectFallbackCheck],
  );

  // Clear timeout for a pending guide
  const clearGuideTimeout = useCallback((sopId: string) => {
    const timeoutId = timeoutRefs.current.get(sopId);
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutRefs.current.delete(sopId);
    }

    const fallbackId = fallbackCheckRefs.current.get(sopId);
    if (fallbackId) {
      clearInterval(fallbackId);
      fallbackCheckRefs.current.delete(sopId);
    }
  }, []);

  // Clear timeout for a direct guide
  const clearDirectGuideTimeout = useCallback((jobId: string) => {
    const timeoutId = directTimeoutRefs.current.get(jobId);
    if (timeoutId) {
      clearTimeout(timeoutId);
      directTimeoutRefs.current.delete(jobId);
    }

    const fallbackId = directFallbackCheckRefs.current.get(jobId);
    if (fallbackId) {
      clearInterval(fallbackId);
      directFallbackCheckRefs.current.delete(jobId);
    }
  }, []);

  // Initialize timeouts for restored pending guides
  const initializeRestoredGuides = useCallback(
    (pendingGuides: Map<string, PendingGuide>) => {
      pendingGuides.forEach((guide) => {
        const { sopId, sopTitle, startedAt, jobId } = guide;
        startTimeout(sopId, sopTitle, startedAt, jobId);

        // If we have a jobId and fallback check is available, perform immediate check
        // (job may have completed while page was closed)
        if (jobId && onFallbackCheck) {
          onFallbackCheck(jobId, sopId);
        }
      });
    },
    [startTimeout, onFallbackCheck],
  );

  // Initialize timeouts for restored pending direct guides
  const initializeRestoredDirectGuides = useCallback(
    (pendingDirectGuides: Map<string, PendingDirectGuide>) => {
      pendingDirectGuides.forEach((guide) => {
        const { jobId, processName, startedAt } = guide;
        startDirectTimeout(jobId, processName, startedAt);

        // Perform immediate check - job may have completed while page was closed
        // (mobile browser backgrounded, screen locked, etc.)
        if (onDirectFallbackCheck) {
          onDirectFallbackCheck(jobId);
        }
      });
    },
    [startDirectTimeout, onDirectFallbackCheck],
  );

  // Cleanup all timeouts and fallback checks on unmount
  useEffect(() => {
    const timeouts = timeoutRefs.current;
    const fallbackChecks = fallbackCheckRefs.current;
    const directTimeouts = directTimeoutRefs.current;
    const directFallbackChecks = directFallbackCheckRefs.current;

    return () => {
      timeouts.forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      timeouts.clear();

      fallbackChecks.forEach((fallbackId) => {
        clearInterval(fallbackId);
      });
      fallbackChecks.clear();

      directTimeouts.forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      directTimeouts.clear();

      directFallbackChecks.forEach((fallbackId) => {
        clearInterval(fallbackId);
      });
      directFallbackChecks.clear();
    };
  }, []);

  return {
    startTimeout,
    startDirectTimeout,
    clearGuideTimeout,
    clearDirectGuideTimeout,
    initializeRestoredGuides,
    initializeRestoredDirectGuides,
  };
}
